import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

/*
  Site content managed by the Super Admin in Admin → Content (CMS).
  Every section has built-in defaults below; once the admin saves a section, the saved version is used.
*/
const IMG = {
  a: "https://images.unsplash.com/photo-1679006831648-7c9ea12e5807?auto=format&fit=crop&w=1200&q=80",
  b: "https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=1200&q=80",
  c: "https://images.unsplash.com/photo-1617627143750-d86bc21e42bb?auto=format&fit=crop&w=1200&q=80",
  d: "https://images.unsplash.com/photo-1641699862936-be9f49b1c38d?auto=format&fit=crop&w=1200&q=80",
  e: "https://images.unsplash.com/photo-1610189012906-4c0aa9b9781e?auto=format&fit=crop&w=1200&q=80",
  f: "https://images.unsplash.com/photo-1678705730064-a7ecbab4b3fb?auto=format&fit=crop&w=1200&q=80",
  g: "https://images.unsplash.com/photo-1618901185975-d59f7091bcfe?auto=format&fit=crop&w=1200&q=80",
  h: "https://images.unsplash.com/photo-1727430228383-aa1fb59db8bf?auto=format&fit=crop&w=1200&q=80",
  i: "https://images.unsplash.com/photo-1609748340041-f5d61e061ebc?auto=format&fit=crop&w=1200&q=80",
  j: "https://images.pexels.com/photos/2723623/pexels-photo-2723623.jpeg?auto=compress&cs=tinysrgb&w=1200",
};
export const CMS_IMAGES = IMG;

export const GOOGLE_REVIEWS_URL = "https://www.google.com/search?q=avishka+navya+collection&rlz=1C1CHBD_enIN1086IN1087&oq=avishka+navya+collection&gs_lcrp=EgZjaHJvbWUqBggAEEUYOzIGCAAQRRg7MgcIARAAGO8FMgcIAhAAGO8FMgcIAxAAGO8FMgoIBBAAGIAEGKIEMgYIBRBFGD0yBggGEEUYPTIGCAcQRRg90gEIMjQwMmowajeoAgCwAgA&sourceid=chrome&source=chrome.ob&ie=UTF-8#lrd=0x3bb8d7638994c45f:0x4a52bf2f23511959,1,,,,";

export const CMS_DEFAULTS = {
  home_intro: {
    enabled: true,
    overline: "The Avishka Navya Promise",
    title: "Handpicked from the looms of India, for the woman who wears her story.",
    text: "Every saree in our boutique is chosen by hand — from the silk houses of Mysore and the workshops of Banaras to the cotton looms of Ilkal. We meet the weavers, we feel the fabric, and we only bring home pieces we would wear ourselves.",
    signature: "Avishka & Navya",
    signature_role: "Founders",
    image: IMG.a,
    stats: [
      { value: "25+", label: "Years of weaving heritage" },
      { value: "5000+", label: "Happy customers" },
      { value: "300+", label: "Weaver families" },
      { value: "100%", label: "Handpicked pieces" },
    ],
    promises: [
      { icon: "truck", title: "Free shipping", text: "On orders above ₹3,000" },
      { icon: "award", title: "Authentic handloom", text: "Certified, direct from weavers" },
      { icon: "scissors", title: "Custom tailoring", text: "Blouse stitching, fall & pico" },
      { icon: "shield", title: "Secure payments", text: "UPI, cards & cash on delivery" },
    ],
  },
  home_sections: {
    categories_overline: "Curated Collections",
    categories_title: "Shop by Categories",
    limelight_overline: "This Season",
    limelight_title: "Products in Limelight",
    limelight_button: "View All",
  },
  home_journey: {
    enabled: true,
    overline: "Our Journey",
    title: "From Loom to Legacy",
    subtitle: "Three generations of weavers, one enduring vision.",
    items: [
      { year: "1978", title: "The First Loom", body: "Our grandmother sets up a single pit loom in a small village near Kanchipuram." },
      { year: "1996", title: "The Family Trade", body: "Master weavers join, expanding into Banarasi silks and traditional Ilkal sarees." },
      { year: "2012", title: "The Boutique", body: "Our first boutique opens, presenting handloom heritage in a modern setting." },
      { year: "2026", title: "Digital Atelier", body: "Avishka Navya Collections launches online — bespoke, borderless, but always handcrafted." },
    ],
  },
  home_testimonials: {
    enabled: true,
    overline: "Loved by",
    title: "Voices from our Sisterhood",
    button_text: "View More Reviews",
    button_link: GOOGLE_REVIEWS_URL,
    items: [
      { name: "Aditi Sharma", role: "Bride, Mumbai", rating: 5, text: "My Kanjivaram from Avishka Navya was the centerpiece of my wedding. The zari work is unreal." },
      { name: "Priya Menon", role: "Bengaluru", rating: 5, text: "Bespoke fit and quick delivery. The customization team is exceptional and so patient." },
      { name: "Radhika Nair", role: "Kochi", rating: 5, text: "Every saree feels like an heirloom. The quality and the packaging are beautiful." },
    ],
  },
  about: {
    hero_overline: "Our Story",
    hero_title: "Woven with reverence.",
    hero_subtitle: "A family boutique bringing India's finest handloom sarees from the weaver's loom to your wardrobe.",
    hero_image: IMG.j,
    story_title: "How our journey started",
    story_text: "Avishka Navya Collections began not as a business, but as a promise — to preserve the fading heritage of Indian handloom in a form that spoke to a new generation.\n\nIt started with our grandmother's single pit loom and a trunk full of sarees she had woven for the women of her village. Years later, two sisters — Avishka and Navya — opened that trunk again and decided these stories deserved a bigger home.\n\nToday we work directly with weavers in Mysore, Banaras, Kanchipuram and Ilkal — no middlemen, no shortcuts. Every piece carries the fingerprint of its maker.",
    story_image: IMG.h,
    quote: "We don't just sell sarees. We carry forward the work of hands that have woven for generations.",
    quote_author: "Avishka & Navya, Founders",
    values: [
      { title: "Direct from weavers", text: "We buy directly from weaving families, so more of every rupee reaches the people who make the saree." },
      { title: "Handpicked, never mass-made", text: "Each piece is checked by hand for weave, colour and finish before it reaches our shelves." },
      { title: "Made to be kept", text: "We choose fabrics that last decades and help you care for them with free advice for life." },
      { title: "Personal service", text: "From blouse stitching to styling for your big day, we treat every customer like family." },
    ],
    stats: [
      { value: "25+", label: "Years in handloom" },
      { value: "300+", label: "Weaver families" },
      { value: "5000+", label: "Happy customers" },
      { value: "6", label: "Weaving regions" },
    ],
    video_title: "Our story in motion",
    video_text: "Step inside the boutique and meet the people behind every saree.",
    video_url: "",
    gallery_title: "Inside the boutique",
    gallery: [IMG.a, IMG.b, IMG.c, IMG.d, IMG.e, IMG.f, IMG.g, IMG.i],
    faq_title: "Frequently asked questions",
    faqs: [
      { q: "Are your sarees genuine handloom?", a: "Yes. Our silk and cotton sarees come directly from weaving clusters, and handloom pieces carry the Handloom Mark where applicable." },
      { q: "Do you offer blouse stitching?", a: "Yes. We offer blouse stitching, fall & pico and custom tailoring. Message us on WhatsApp or visit the boutique." },
      { q: "How long does delivery take?", a: "Orders are dispatched within 2 working days. Delivery usually takes 3–7 working days within India." },
      { q: "Is shipping free?", a: "Shipping is free on orders above ₹3,000. A small shipping charge applies to smaller orders." },
      { q: "Can I return or exchange a saree?", a: "Unused sarees with tags can be exchanged within 7 days of delivery. Custom-stitched items cannot be returned." },
      { q: "Which payment methods do you accept?", a: "UPI, debit and credit cards, net banking (via Razorpay) and Cash on Delivery." },
      { q: "How should I care for a silk saree?", a: "Dry clean only, store in a muslin cloth and refold every few months. Read our Blog for a detailed care guide." },
    ],
    cta_title: "Visit our boutique",
    cta_text: "See the colours in person, feel the silk and get styled by our team.",
  },
  lookbook: {
    overline: "Editorial",
    title: "The Lookbook",
    intro: "The Lookbook is our style journal — real outfits styled by our team to show you how each saree can be worn: for weddings, festivals, office and everyday. Tap any look to shop the pieces.",
    looks: [
      { title: "The Mysore Bride", occasion: "Wedding", description: "Emerald Mysore silk with temple gold and fresh jasmine.", image: IMG.a, link: "/shop/pure-mysore-silk-sarees", link_text: "Shop Mysore silk" },
      { title: "Banarasi Evenings", occasion: "Reception", description: "Crimson crepe Banarasi with a contrast blouse and statement earrings.", image: IMG.j, link: "/shop/pure-crepe-banarasi-sarees", link_text: "Shop Banarasi" },
      { title: "Festive Pink", occasion: "Festival", description: "Rani pink silk paired with polki and a low bun.", image: IMG.c, link: "/shop/silk-sarees", link_text: "Shop silk sarees" },
      { title: "Royal Purple", occasion: "Party", description: "Purple and gold with a belted drape for a modern silhouette.", image: IMG.d, link: "/shop/fancy-banarasi", link_text: "Shop Fancy Banarasi" },
      { title: "Monsoon Blue", occasion: "Day function", description: "Light semi silk in blue and yellow — easy to wear all day.", image: IMG.e, link: "/shop/semi-silk-sarees", link_text: "Shop semi silk" },
      { title: "Ivory Classic", occasion: "Pooja", description: "Ivory and red cotton with minimal gold.", image: IMG.f, link: "/shop/cotton-sarees", link_text: "Shop cotton sarees" },
    ],
    tips_title: "Styling notes",
    tips: [
      { title: "Choose the blouse first", text: "A contrast blouse modernises a classic saree; a matching one keeps it timeless." },
      { title: "Let one thing shine", text: "Heavy zari? Keep jewellery simple. Plain silk? Go bold with jewellery." },
      { title: "Pleats make the look", text: "Five to seven neat pleats, pinned at the waist, give the cleanest fall." },
    ],
    cta_title: "Find your next favourite saree",
    cta_text: "Browse the full collection, or visit the boutique to see the colours in person.",
  },
  whatsapp: {
    enabled: true,
    phone: "",
    side: "right",
    title: "Avishka Navya Collections",
    subtitle: "Typically replies within an hour",
    greeting: "Namaste! 🙏 How can we help you today?",
    messages: [
      { label: "I want to know about a saree", text: "Hello! I'd like to know more about a saree on your website." },
      { label: "Check availability & price", text: "Hi, could you please share the availability and price of this saree?" },
      { label: "Blouse stitching / custom order", text: "Hello, I'm interested in blouse stitching / a custom order." },
      { label: "Track my order", text: "Hi, I'd like to know the status of my order." },
      { label: "Visit the boutique", text: "Hello, what are your boutique timings and location?" },
    ],
  },
  footer: {
    tagline: "Heirloom sarees, kurtis, and bespoke tailored pieces — woven by master artisans across India.",
    copyright: "© 2026 Avishka Navya Collections. Handcrafted with care.",
  },
  auth: {
    login_title: "Welcome back",
    login_subtitle: "Sign in to your Avishka Navya account",
    register_title: "Join our atelier",
    register_subtitle: "Create an account to save pieces you love and track orders",
    quotes: [
      "Every thread tells a story.",
      "Handpicked from the looms of India.",
      "Heirlooms for the modern woman.",
    ],
    images: [IMG.a, IMG.c, IMG.d, IMG.e, IMG.b, IMG.f],
  },
};

/** Saved content for a section, falling back to the defaults for any field that was never set. */
export function useCms(key) {
  const { data, isLoading } = useQuery({
    queryKey: ["cms", key],
    queryFn: () => api.get(`/cms/${key}`).then((r) => r.data?.data || null),
    staleTime: 5 * 60 * 1000,
  });
  const defaults = CMS_DEFAULTS[key] || {};
  return { ...defaults, ...(data || {}), _loading: isLoading, _saved: !!data };
}
